import * as React from 'react'

import { ServiceDialog } from '@user/components/Services'

import {
  AgreementRequirements,
  PaymentRequirements,
  SchedulingClosingRequirement,
  ServiceRequirements,
} from './Requirements'
import { useData, useHandlers } from './hooks'
import type { RequirementsToCloseProps } from './typings'
import { ManageContainerHeader } from './ManageContainer'
import { ClosingRequirementsTableList } from './ClosingRequirementTables'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'

export function RequirementsToClose(props: RequirementsToCloseProps) {
  const {
    entityId,
    canUpdate,
    externalLocationId,
    externalBillToId,
    hasValidLocationAddress,
    leadStatus,
    oppStatus,
    referralDiscountId,
    requirementsAndCounts,
    scheduleSettings,
    hideHeader,
    servicesCount,
  } = props

  const {
    recordClosed,
    agreementIsComplete,
    deleteServiceWarningMessage,
    isDisqualifiedLead,
    minimumServicesCount,
    oppIsClosed,
    paymentIsComplete,
    salesFunnel,
    showScheduling,
  } = useData(props)

  const {
    svcOpen,
    isAddingService,
    openedFromAgreement,
    onOpenSvc,
    onCloseSvc,
    onOpenAndAddSvc,
    onOpenSvcFromAgreement,
    openCreateDrawer,
    createDrawerOpen,
    setCreateDrawerOpen,
  } = useHandlers(props)

  return (
    <>
      <Box>
        {!hideHeader && (
          <ManageContainerHeader
            salesFunnel={salesFunnel}
            requirements={requirementsAndCounts}
          />
        )}

        <Stack direction="row">
          <Box ml="auto" />
          <Stack p={1} spacing={2} direction="row" overflow="auto hidden">
            <ServiceRequirements
              {...props}
              onOpenAndAddSvc={onOpenAndAddSvc}
              onOpenSvc={onOpenSvc}
            />

            <AgreementRequirements
              {...props}
              count={requirementsAndCounts.agreementsCount}
              isComplete={agreementIsComplete}
              openServicesDrawer={onOpenSvcFromAgreement}
              leadStatus={leadStatus}
              oppStatus={oppStatus}
              salesFunnel={salesFunnel}
              openCreateDrawer={openCreateDrawer}
              createDrawerOpen={createDrawerOpen}
              setCreateDrawerOpen={setCreateDrawerOpen}
            />

            <PaymentRequirements
              {...props}
              count={requirementsAndCounts.paymentsCount}
              isComplete={paymentIsComplete}
              locationId={externalLocationId}
              billToId={externalBillToId}
              leadStatus={leadStatus}
              oppStatus={oppStatus}
            />

            {showScheduling && scheduleSettings && (
              <SchedulingClosingRequirement
                {...props}
                entityId={entityId}
                recordClosed={recordClosed}
                scheduleSettings={scheduleSettings}
                externalLocationId={externalLocationId}
                hasValidLocationAddress={hasValidLocationAddress}
              />
            )}
          </Stack>
          <Box mr="auto" />
        </Stack>

        <ClosingRequirementsTableList
          {...props}
          entityClosed={recordClosed}
          billToId={externalBillToId}
          openAgreementCreateDrawer={openCreateDrawer}
        />
      </Box>

      {/* ========== S E R V I C E   D I A L O G ========== */}
      <ServiceDialog
        {...props}
        minimumServicesCount={minimumServicesCount}
        deleteServiceWarningMessage={deleteServiceWarningMessage}
        open={svcOpen}
        onClose={onCloseSvc}
        openAddServiceDrawerOnMount={isAddingService}
        readOnly={!canUpdate || oppIsClosed || isDisqualifiedLead}
        locationSource="existing"
        referralDiscountId={referralDiscountId}
        totalServicesCount={servicesCount}
        closeButtonText={
          openedFromAgreement
            ? 'Back to Agreement'
            : 'Close Services and Products'
        }
      />
    </>
  )
}
